'use client';
import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { OrdersModal } from './orders-modal';

// Sample data - in a real app, this would come from an API
const smsCampaigns = [
  {
    id: 'sms-001',
    name: 'Weekend Flash Sale',
    sent: 3250,
    openRate: '—',
    clickRate: '—',
    revenue: '$4,820',
    unsubRate: '0.2%',
  },
  {
    id: 'sms-002',
    name: 'Appointment Reminder',
    sent: 1850,
    openRate: '—',
    clickRate: '—',
    revenue: '$2,340',
    unsubRate: '0.1%',
  },
  {
    id: 'sms-003',
    name: 'Limited Time Offer',
    sent: 2750,
    openRate: '—',
    clickRate: '—',
    revenue: '$6,120',
    unsubRate: '0.3%',
  },
  {
    id: 'sms-004',
    name: 'New Arrival Alert',
    sent: 2100,
    openRate: '—',
    clickRate: '—',
    revenue: '$3,580',
    unsubRate: '0.2%',
  },
  {
    id: 'sms-005',
    name: 'Customer Feedback Request',
    sent: 1680,
    openRate: '—',
    clickRate: '—',
    revenue: '$0',
    unsubRate: '0.4%',
  },
];

interface SmsCampaignsTableProps {
  timeRange: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function SmsCampaignsTable({ timeRange }: SmsCampaignsTableProps) {
  const [selectedCampaign, setSelectedCampaign] = useState<{
    name: string;
    revenue: string;
  } | null>(null);

  const handleRevenueClick = (campaign: { name: string; revenue: string }) => {
    setSelectedCampaign(campaign);
  };

  return (
    <div className='relative'>
      <div className='overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent -webkit-overflow-scrolling-touch'>
        <div className='min-w-[700px] p-6'>
          <Table>
            <TableHeader>
              <TableRow className='border-b border-gray-200 hover:bg-transparent'>
                <TableHead className='w-[250px] bg-white sticky left-0 z-10 font-medium text-sm uppercase tracking-wider'>
                  Campaign Name
                </TableHead>
                <TableHead className='text-right font-medium text-sm uppercase tracking-wider'>
                  Sent
                </TableHead>
                <TableHead className='text-right font-medium text-sm uppercase tracking-wider'>
                  Open Rate
                </TableHead>
                <TableHead className='text-right font-medium text-sm uppercase tracking-wider'>
                  Click Rate
                </TableHead>
                <TableHead className='text-right font-medium text-sm uppercase tracking-wider'>
                  Revenue
                </TableHead>
                <TableHead className='text-right font-medium text-sm uppercase tracking-wider'>
                  Unsub Rate
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {smsCampaigns.map(campaign => (
                <TableRow
                  key={campaign.id}
                  className='hover:bg-gray-50 cursor-pointer transition-colors text-md'
                >
                  <TableCell className='font-medium !border-0 bg-white sticky left-0 z-10 flex items-center gap-2'>
                    {campaign.name}
                    <ChevronRight className='h-4 w-4 text-muted-foreground opacity-50' />
                  </TableCell>
                  <TableCell className='text-right !border-0'>
                    {campaign.sent.toLocaleString()}
                  </TableCell>
                  <TableCell className='text-right !border-0'>
                    {campaign.openRate}
                  </TableCell>
                  <TableCell className='text-right !border-0'>
                    {campaign.clickRate}
                  </TableCell>
                  <TableCell className='text-right !border-0'>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleRevenueClick({
                          name: campaign.name,
                          revenue: campaign.revenue,
                        });
                      }}
                      className={`font-medium ${
                        campaign.revenue !== '$0'
                          ? 'text-secondary hover:text-secondary/80 hover:underline'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {campaign.revenue}
                    </button>
                  </TableCell>
                  <TableCell className='text-right !border-0'>
                    {campaign.unsubRate}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <div className='absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent pointer-events-none z-20 hidden sm:block'></div>
      <div className='absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent pointer-events-none z-20 hidden sm:block'></div>
      <div className='text-xs text-muted-foreground p-4 text-center sm:hidden'>
        Swipe horizontally to view all data →
      </div>

      {selectedCampaign && (
        <OrdersModal
          isOpen={!!selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          campaignName={selectedCampaign.name}
          revenue={selectedCampaign.revenue}
        />
      )}
    </div>
  );
}
